import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, Inject, ViewChild } from "@angular/core";
import { ValidatorFn } from "@angular/forms";
import { EMPTY, catchError, finalize, take } from "rxjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { KpiLastSnapshot } from "@webapp/kpis/models/kpi-snapshot.models";
import { Kpi } from "@webapp/kpis/models/kpis.models";
import { KpiSnapshotsFacade } from "@webapp/kpis/services/kpi-snapshots/kpi-snapshots-facade.service";
import { dateValidator, isDateAfterAllowedAccessibleFutureDate } from "@webapp/kpis/utils";
import { FormGroupComponent } from "@webapp/shared/form/form-group.component";
import dayjs from "@webapp/shared/libs/dayjs";
import { UiModalRef } from "@webapp/ui/modal/abstracts/modal-ref";
import { UI_MODAL_DATA } from "@webapp/ui/modal/modal.models";
import { CHART_POINT_DATE_FORMAT } from "../kpi-details/kpi-chart/utils";

export type KpiSnapshotFormModalData = {
  kpi: Kpi;
  lastSnapshot: KpiLastSnapshot;
};

@Component({
  selector: "kpi-snapshot-form",
  templateUrl: "./kpi-snapshot-form.component.html",
  styleUrls: ["kpi-snapshot-form.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiSnapshotFormComponent {
  public kpi: Kpi;
  public lastSnapshot: KpiLastSnapshot;

  @ViewChild(FormGroupComponent)
  public form: FormGroupComponent<{
    value: number;
    date: Date;
  }>;

  public dateValidators: ValidatorFn[] = [dateValidator];

  public isHttpRequestInProgress = false;

  constructor(
    private modalRef: UiModalRef,
    private kpiSnapshotsFacade: KpiSnapshotsFacade,
    private uiErrorHandlingService: UIErrorHandlingService,
    private broadcastService: BroadcastService,
    private accountResolverService: AccountResolverService,
    @Inject(UI_MODAL_DATA) modalData: KpiSnapshotFormModalData
  ) {
    Object.assign(this, modalData);
  }

  public onSubmit(): void {
    this.isHttpRequestInProgress = true;

    this.kpiSnapshotsFacade
      .createKpiSnapshot$(this.kpi.id, {
        group: dayjs(new Date(this.form.value.date)).format(CHART_POINT_DATE_FORMAT),
        value: this.form.value.value,
        timeZone: this.accountResolverService.getTimezone(),
      })
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));
          this.onClose(false);
          return EMPTY;
        }),
        finalize(() => (this.isHttpRequestInProgress = false))
      )
      .subscribe(() => {
        // to do: handle changes via an observable service - edit, delete, create snapshot in both directions - https://quantive-inc.atlassian.net/browse/GVS-33860
        // the KPIs table logic when a snapshot is created is pretty complicated, this is why we're not passing any args to the event
        this.broadcastService.emit("kpiSnapshotCreatedViaForm");
        this.onClose(true);
      });
  }

  public onClose(isSnapshotUpdated = false): void {
    this.modalRef.destroy(isSnapshotUpdated);
  }

  public isDateDisabled(date: Date): boolean {
    return isDateAfterAllowedAccessibleFutureDate(date);
  }
}
